import {
  Modal,
  Box,
  Text,
  Group,
  Kbd,
  Stack,
  SimpleGrid,
  useMantineTheme,
  Divider,
} from '@mantine/core';
import { useHotkeys } from '@mantine/hooks';
import { forwardRef } from 'react';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface ShortcutItem {
  keys: string[];
  description: string;
  group?: string;
}

export interface ShortcutHelperProps {
  shortcuts: ShortcutItem[];
  opened: boolean;
  onClose: () => void;
  title?: string;
  columns?: number;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
}

const ShortcutHelper = forwardRef<HTMLDivElement, ShortcutHelperProps>(
  (
    {
      shortcuts,
      opened,
      onClose,
      title = 'Keyboard Shortcuts',
      columns = 2,
      gradientVariant = 'primary',
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Group shortcuts by category
    const groupedShortcuts = shortcuts.reduce((acc, shortcut) => {
      const group = shortcut.group || 'General';
      if (!acc[group]) acc[group] = [];
      acc[group].push(shortcut);
      return acc;
    }, {} as Record<string, ShortcutItem[]>);

    const formatKey = (key: string): string => {
      const keyMap: Record<string, string> = {
        mod: navigator.platform.includes('Mac') ? '⌘' : 'Ctrl',
        ctrl: navigator.platform.includes('Mac') ? '⌃' : 'Ctrl',
        alt: navigator.platform.includes('Mac') ? '⌥' : 'Alt',
        shift: '⇧',
        enter: '↵',
        escape: 'Esc',
        backspace: '⌫',
        delete: '⌦',
        up: '↑',
        down: '↓',
        left: '←',
        right: '→',
        space: 'Space',
        tab: 'Tab',
      };
      return keyMap[key.toLowerCase()] || key.toUpperCase();
    };

    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Text fw={600} size="lg" style={accessibilityStyles}>
            {title}
          </Text>
        }
        size="lg"
        centered
      >
        <Box ref={ref}>
          <Stack gap="lg">
            {Object.entries(groupedShortcuts).map(([group, items], groupIndex) => (
              <Box key={group}>
                {groupIndex > 0 && <Divider mb="md" />}
                <Text
                  size="sm"
                  fw={600}
                  c={gradient?.from}
                  mb="sm"
                  tt="uppercase"
                  style={accessibilityStyles}
                >
                  {group}
                </Text>
                <SimpleGrid cols={columns} spacing="sm">
                  {items.map((shortcut, index) => (
                    <Group key={index} justify="space-between" wrap="nowrap">
                      <Text size="sm" c="dimmed" style={{ ...accessibilityStyles, flex: 1 }}>
                        {shortcut.description}
                      </Text>
                      <Group gap={4} wrap="nowrap">
                        {shortcut.keys.map((key, keyIndex) => (
                          <Kbd key={keyIndex} size="sm">
                            {formatKey(key)}
                          </Kbd>
                        ))}
                      </Group>
                    </Group>
                  ))}
                </SimpleGrid>
              </Box>
            ))}
          </Stack>

          <Box
            mt="lg"
            pt="md"
            style={{ borderTop: `1px solid ${theme.colors.gray[2]}` }}
          >
            <Group justify="center" gap="xs">
              <Text size="xs" c="dimmed" style={accessibilityStyles}>
                Press
              </Text>
              <Kbd size="xs">?</Kbd>
              <Text size="xs" c="dimmed" style={accessibilityStyles}>
                to toggle this help
              </Text>
            </Group>
          </Box>
        </Box>
      </Modal>
    );
  }
);

ShortcutHelper.displayName = 'ShortcutHelper';

export default ShortcutHelper;
